import { useSetProductFlag } from '../../hooks/useProducts';
import { ProductFlagType } from '../../types/product-flag-type.enum';
import { useRemoveProductsFromCustomSet } from '@modules/custom-sets';
import styles from './ProductCard.module.css';
import { notifications } from '@mantine/notifications';
import {
    IoAlbumsOutline,
    IoClose,
    IoCloseCircleOutline,
    IoEyeOffOutline,
    IoFlameOutline,
    IoPricetagOutline,
    IoRocketOutline,
    IoSparklesOutline,
} from 'react-icons/io5';

interface ProductBadgesProps {
    productId?: number;
    hasDiscount: boolean;
    isPopular?: boolean;
    isNew?: boolean;
    isHidden?: boolean;
    isOutOfStock?: boolean;
    promotionEnabled?: boolean;
    customSets?: Array<{
        id: number;
        name: string;
    }>;
    direction?: 'row' | 'column';
    onPopularClick?: () => void;
    onHiddenClick?: () => void;
}

export default function ProductBadges({
    productId,
    hasDiscount,
    isPopular,
    isNew,
    isHidden,
    isOutOfStock,
    promotionEnabled,
    customSets,
    direction = 'column',
    onPopularClick,
    onHiddenClick,
}: ProductBadgesProps) {
    const setProductFlag = useSetProductFlag(ProductFlagType.POPULAR);
    const setNewFlag = useSetProductFlag(ProductFlagType.NEW);
    const setHiddenFlag = useSetProductFlag(ProductFlagType.HIDDEN);
    const setOutOfStockFlag = useSetProductFlag(ProductFlagType.OUT_OF_STOCK);
    const removeFromCustomSet = useRemoveProductsFromCustomSet();

    const handleRemove = async (type: 'popular' | 'new' | 'hidden' | 'out-of-stock' | 'customSet', customSetId?: number) => {
        // Если переданы обработчики кликов (для категорий), используем их
        if (type === 'popular' && onPopularClick) {
            onPopularClick();
            return;
        }
        if (type === 'hidden' && onHiddenClick) {
            onHiddenClick();
            return;
        }

        // Иначе используем логику для товаров
        if (!productId) return;

        try {
            if (type === 'customSet' && customSetId) {
                await removeFromCustomSet.mutateAsync({
                    id: customSetId,
                    productIds: [productId],
                });
            } else {
                const mutation = type === 'popular' ? setProductFlag :
                    type === 'new' ? setNewFlag :
                        type === 'hidden' ? setHiddenFlag :
                            setOutOfStockFlag;

                await mutation.mutateAsync({
                    id: productId,
                    value: false,
                });
            }

            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно удален из подборки',
                color: 'green',
            });
        } catch (error) {
            console.error('Ошибка при удалении из подборки:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении из подборки',
                color: 'red',
            });
        }
    };

    return (
        <div
            className={styles.productCardBadges}
            style={{ flexDirection: direction }}
        >
            {promotionEnabled && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgePromotion}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoRocketOutline size={12} aria-hidden />
                    продвижение
                </span>
            )}
            {hasDiscount && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgeSale}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoPricetagOutline size={12} aria-hidden />
                    акция
                </span>
            )}
            {isPopular && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgePopular}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoFlameOutline size={12} aria-hidden />
                    популярное
                </span>
            )}
            {isNew && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgeNew}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoSparklesOutline size={12} aria-hidden />
                    новинка
                </span>
            )}
            {isHidden && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgeHidden}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoEyeOffOutline size={12} aria-hidden />
                    скрыто
                </span>
            )}
            {isOutOfStock && (
                <span
                    className={`${styles.productCardBadge} ${styles.productCardBadgeOut}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IoCloseCircleOutline size={12} aria-hidden />
                    нет в наличии
                </span>
            )}
            {customSets && customSets.length > 0 && (
                customSets.map((customSet) => (
                    <span
                        key={customSet.id}
                        className={styles.productCardBadge}
                        title={customSet.name}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IoAlbumsOutline size={12} aria-hidden />
                        {customSet.name}
                        <button
                            type="button"
                            className={styles.productCardBadgeRemove}
                            disabled={removeFromCustomSet.isPending}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove('customSet', customSet.id);
                            }}
                            title="Удалить из подборки"
                            aria-label={`Удалить из подборки «${customSet.name}»`}
                        >
                            <IoClose size={12} aria-hidden />
                        </button>
                    </span>
                ))
            )}
        </div>
    );
}

